import { useEffect } from "react";
import { useFeatureIsOn, useGrowthBook } from "@growthbook/growthbook-react";
import { ASSISTANT_ENGINE_FLAG } from "../../../../api/src/shared/growthbook-flags";
import { memoryStore, useStore } from "../../lib/store";
import { growthbookIdentityStore } from "../../tools/growthbook";
import { useCurrentUser } from "../user/hooks/useCurrentUser";
import type { ConversationEngine } from "./conversationEngine";

// `initGrowthbook()` has no timeout: a payload this late is treated like the CDN outage `App` answers with every flag off.
const PAYLOAD_DEADLINE_MS = 3000;

type Payload = "ready" | "waiting" | "overdue";

// The first outcome holds for the session: a payload landing after the deadline must not switch engines mid-conversation.
function settlePayload(current: Payload, event: "ready" | "deadline"): Payload {
  if (current !== "waiting") return current;
  return event === "ready" ? "ready" : "overdue";
}

// One outcome and one deadline for every reader, so the history panel and the composer agree.
const payloadStore = memoryStore<Payload>("waiting");
let deadline: ReturnType<typeof setTimeout> | null = null;

// A flag read before the payload lands, or on anonymous attributes, says "off"
// and looks legitimate (`.knowledge/growthbook-oneshot-flag-race.md`).
export function resolveConversationEngine(input: {
  readonly userUid: string | null;
  readonly identity: {
    readonly uid: string;
    readonly status: "synced" | "failed";
  } | null;
  readonly payload: Payload;
  readonly isAssistant: boolean;
}): ConversationEngine | "pending" {
  if (input.userUid === null) return "pending";
  if (input.identity === null || input.identity.uid !== input.userUid)
    return "pending";
  if (input.identity.status === "failed") return "legacy";
  if (input.payload === "waiting") return "pending";
  if (input.payload === "overdue") return "legacy";
  return input.isAssistant ? "assistant" : "legacy";
}

export function useConversationEngine(): ConversationEngine | "pending" {
  const growthbookClient = useGrowthBook();
  const { currentUser } = useCurrentUser();
  const [identity] = useStore(growthbookIdentityStore);
  const isAssistant = useFeatureIsOn(ASSISTANT_ENGINE_FLAG);
  const [stored, setPayload] = useStore(payloadStore);
  const ready = growthbookClient.ready;
  const payload = ready ? settlePayload(stored, "ready") : stored;

  useEffect(() => {
    if (ready) {
      setPayload((current) => settlePayload(current, "ready"));
      return;
    }
    if (deadline !== null) return;
    // Read at the deadline: a render may already show a payload its effect has not stored yet.
    deadline = setTimeout(() => {
      setPayload((current) =>
        settlePayload(current, growthbookClient.ready ? "ready" : "deadline")
      );
    }, PAYLOAD_DEADLINE_MS);
  }, [ready, growthbookClient, setPayload]);

  return resolveConversationEngine({
    userUid: currentUser === null ? null : currentUser.uid,
    identity,
    payload,
    isAssistant,
  });
}
